import { resolveSymbolImage, pointPresentation, symbolDisplayLongEdge } from '../symbols/scene';
import { isImageGraphic } from '../symbols/model';
import { EMPTY_SYMBOL_CATALOGUE, type PreparedSymbolCatalogue } from '../symbols/images';
import { UI_ACCENT_COLOUR } from '../styles/uiTokens';
import { presentationDimension, presentationScale } from './presentation';
import type { Feature, FeatureCollection } from 'geojson';
import type {
  AddLayerObject,
  ExpressionSpecification,
  FilterSpecification,
  SourceSpecification,
  SymbolLayerSpecification,
} from 'maplibre-gl';

import {
  isVectorSource,
  type FeatureProperties,
  type SourceDescriptor,
  type SupportedGeometry,
  type VectorFeatureCollection,
} from '../data/model';
import type { LayerDefinition } from '../project/model';
import { isLayerLabelConfigurationValid } from '../labels/validation';
import { isLayerPopupConfigurationValid, popupConfigurationHasContent } from '../popups/validation';
import { numericClassesForClassification } from '../styles/classification';
import { effectiveSymbols } from '../styles/effectiveSymbols';
import { mapLibreAttributeRuleExpression } from './mapLibreAttributeRule';
import { mapLibreLabelFontStack } from './mapLibreLabelFonts';
import { polygonLabelAnchors } from './polygonLabelAnchors';
import { lineLabelPaths } from './lineLabelPaths';
import type {
  CategoryValue,
  PointSymbol,
  ThematicSymbolDefinition,
  VectorLayerStyle,
  VectorSymbols,
} from '../styles/model';
import type { InspectionHighlightGeometry, RenderScene } from './renderingAdapter';
import { effectiveVectorSourceForLayer } from '../joins/effectiveLayer';
import { createAnnotationRenderPlan } from '../annotations/mapLibre';
import type { AnnotationScreenProjection } from '../annotations/textLayout';

export interface MapLibreRenderSource {
  readonly id: string;
  readonly data: VectorFeatureCollection;
  /** Renderer feature index to canonical source feature index. */
  readonly sourceFeatureIndices: readonly number[];
  readonly definition: SourceSpecification;
}

export interface MapLibreRenderImage {
  readonly id: string;
  readonly width: number;
  readonly height: number;
  readonly data: Uint8Array;
  readonly pixelRatio: number;
  readonly sdf?: boolean;
  readonly stretchX?: [number, number][];
  readonly stretchY?: [number, number][];
  readonly content?: [number, number, number, number];
}

export interface MapLibreRenderPlan {
  readonly sources: readonly MapLibreRenderSource[];
  readonly images: readonly MapLibreRenderImage[];
  readonly layers: readonly AddLayerObject[];
  readonly popupLayers: readonly MapLibrePopupLayer[];
  readonly selectableLayers: readonly MapLibreSelectableLayer[];
  readonly annotationSelectableLayerIds: readonly string[];
}

export interface MapLibreSelectableLayer {
  readonly renderLayerId: string;
  readonly renderSourceId: string;
  readonly geometry: InspectionHighlightGeometry;
  readonly highlightBeforeRenderLayerId: string;
  readonly layerId: string;
  readonly sourceId: string;
  /** Zero is the visually topmost authored layer. */
  readonly stackIndex: number;
}

export type MapLibrePopupLayer = MapLibreSelectableLayer;

function sourceRenderId(sourceId: string): string {
  return `dropmap-source-${sourceId}`;
}

function sourceRenderIdForLayer(layer: LayerDefinition, sourceId: string): string {
  return (layer.joins ?? []).length > 0
    ? `${sourceRenderId(sourceId)}-joined-${layer.id}`
    : sourceRenderId(sourceId);
}

export const LINE_LABEL_REPEAT_DISTANCE = 480;

export function lineLabelSourceRenderId(layerId: string): string {
  return `dropmap-line-labels-${layerId}`;
}

function polygonLabelSourceRenderId(renderSourceId: string): string {
  return `${renderSourceId}-polygon-labels`;
}

function layerRenderId(
  layerId: string,
  part:
    | 'label-line'
    | 'label-point'
    | 'label-polygon'
    | 'line'
    | 'point'
    | 'polygon'
    | 'polygon-outline',
): string {
  return `dropmap-layer-${layerId}-${part}`;
}

function categoryCondition(field: string, value: CategoryValue): ExpressionSpecification {
  if (value.kind === 'missing') return ['!', ['has', field]];
  if (value.kind === 'null') return ['all', ['has', field], ['==', ['get', field], null]];
  return ['==', ['get', field], value.value];
}

type SymbolValue = string | number | number[];

function expressionValue<T extends SymbolValue>(value: T): T | ExpressionSpecification {
  return (Array.isArray(value) ? ['literal', value] : value) as T | ExpressionSpecification;
}

function classifiedValue<T extends SymbolValue>(
  style: VectorLayerStyle,
  valueFor: (symbols: VectorSymbols) => T,
): T | ExpressionSpecification {
  const classification = style.classification;
  if (classification.mode === 'single') return valueFor(style.symbols);
  if (classification.mode === 'categorised') {
    const expression: unknown[] = ['case'];
    for (const category of classification.categories) {
      expression.push(
        categoryCondition(classification.field, category.value),
        expressionValue(valueFor(effectiveSymbols(style.symbols, category.symbol))),
      );
    }
    expression.push(
      expressionValue(valueFor(effectiveSymbols(style.symbols, classification.other))),
    );
    return expression as ExpressionSpecification;
  }

  const numericClasses = numericClassesForClassification(classification);
  const fallback = expressionValue(
    valueFor(effectiveSymbols(style.symbols, classification.nullSymbol)),
  );
  const firstClass = numericClasses[0];
  if (!firstClass) return fallback as ExpressionSpecification;
  const numericValue: ExpressionSpecification = ['number', ['get', classification.field]];
  const firstValue = expressionValue(valueFor(effectiveSymbols(style.symbols, firstClass.symbol)));
  const classified: ExpressionSpecification =
    numericClasses.length === 1
      ? (firstValue as ExpressionSpecification)
      : ([
          'step',
          numericValue,
          firstValue,
          ...numericClasses
            .slice(1)
            .flatMap((numericClass, index) => [
              numericClasses[index]?.upper ?? numericClass.lower,
              expressionValue(valueFor(effectiveSymbols(style.symbols, numericClass.symbol))),
            ]),
        ] as ExpressionSpecification);
  return [
    'case',
    ['==', ['typeof', ['get', classification.field]], 'number'],
    classified,
    fallback,
  ] as ExpressionSpecification;
}

import { MAP_LINE_DASHES } from '../styles/lineDash';
export const MAPLIBRE_DASH_ARRAYS = MAP_LINE_DASHES;

function geometryFilter(type: 'LineString' | 'Point' | 'Polygon'): ['==', string, string] {
  return ['==', '$type', type];
}

function geometryDisplayFilter(
  layer: LayerDefinition,
  type: 'LineString' | 'Point' | 'Polygon',
): FilterSpecification {
  return [
    'all',
    ['==', ['geometry-type'], type],
    ...(layer.displayFilter ? [mapLibreAttributeRuleExpression(layer.displayFilter)] : []),
  ] as FilterSpecification;
}

const POINT_LABEL_ANCHORS = {
  above: 'bottom',
  below: 'top',
  centre: 'center',
  left: 'right',
  right: 'left',
} as const;

function scalarLabelValueCondition(field: string): ExpressionSpecification {
  return ['match', ['typeof', ['get', field]], ['string', 'number', 'boolean'], true, false];
}

function labelFilter(
  layer: LayerDefinition,
  geometry: 'LineString' | 'Point' | 'Polygon',
): FilterSpecification {
  const field = layer.labels.field;
  if (!field) throw new Error('A validated label layer requires a label field.');
  return [
    'all',
    ['==', ['geometry-type'], geometry],
    scalarLabelValueCondition(field),
    ...(layer.displayFilter ? [mapLibreAttributeRuleExpression(layer.displayFilter)] : []),
    ...(layer.labels.filter ? [mapLibreAttributeRuleExpression(layer.labels.filter)] : []),
  ] as FilterSpecification;
}

function labelText(field: string): ExpressionSpecification {
  return ['case', scalarLabelValueCondition(field), ['to-string', ['get', field]], ''];
}

function labelPaint(
  layer: LayerDefinition,
  scale: number,
): NonNullable<SymbolLayerSpecification['paint']> {
  return {
    'text-color': layer.labels.textColour,
    'text-halo-color': layer.labels.haloColour,
    'text-halo-width': presentationDimension(layer.labels.haloWidth, scale),
  };
}

function labelLayout(
  layer: LayerDefinition,
  field: string,
  scale: number,
): NonNullable<SymbolLayerSpecification['layout']> {
  return {
    visibility: layer.visible ? 'visible' : 'none',
    'text-field': labelText(field),
    'text-font': [...mapLibreLabelFontStack(layer.labels)],
    'text-size': presentationDimension(layer.labels.textSize, scale, 2),
    'text-padding': presentationDimension(layer.labels.collisionSpacing, scale),
    'text-allow-overlap': false,
    'text-ignore-placement': false,
  };
}

interface MapLibreData {
  readonly data: FeatureCollection<SupportedGeometry, FeatureProperties>;
  readonly sourceFeatureIndices: readonly number[];
}

const mapLibreDataCache = new WeakMap<VectorFeatureCollection, MapLibreData>();

function dataForMapLibre(data: VectorFeatureCollection): MapLibreData {
  const cached = mapLibreDataCache.get(data);
  if (cached) return cached;
  const features: Feature<SupportedGeometry, FeatureProperties>[] = [];
  const sourceFeatureIndices: number[] = [];
  data.features.forEach((feature, sourceFeatureIndex) => {
    if (feature.geometry === null) return;
    features.push(feature as Feature<SupportedGeometry, FeatureProperties>);
    sourceFeatureIndices.push(sourceFeatureIndex);
  });
  const rendererData = {
    data: { type: 'FeatureCollection' as const, features },
    sourceFeatureIndices,
  };
  mapLibreDataCache.set(data, rendererData);
  return rendererData;
}

function hexToRgb(colour: string): readonly [number, number, number] {
  return [
    Number.parseInt(colour.slice(1, 3), 16),
    Number.parseInt(colour.slice(3, 5), 16),
    Number.parseInt(colour.slice(5, 7), 16),
  ];
}

function assetImage(symbol: PointSymbol, catalogue: PreparedSymbolCatalogue) {
  return resolveSymbolImage(
    catalogue,
    symbol.shape,
    symbol.fillColor,
    symbol.tint,
    pointPresentation(symbol),
  );
}
function pointImageId(
  symbol: PointSymbol,
  catalogue: PreparedSymbolCatalogue = EMPTY_SYMBOL_CATALOGUE,
): string {
  if (isImageGraphic(symbol.shape)) return assetImage(symbol, catalogue).id;
  return [
    'dropmap-point',
    symbol.shape,
    symbol.fillColor.slice(1),
    symbol.outlineColor.slice(1),
    symbol.radius,
    symbol.outlineWidth,
    symbol.opacity,
  ]
    .join('-')
    .replaceAll('.', '_');
}

function triangleContains(x: number, y: number, radius: number): boolean {
  const vertices = [
    [0, -radius * 1.15],
    [-radius, radius * 0.85],
    [radius, radius * 0.85],
  ] as const;
  let sign = 0;
  for (let index = 0; index < vertices.length; index += 1) {
    const start = vertices[index];
    const end = vertices[(index + 1) % vertices.length];
    if (!start || !end) continue;
    const cross = (x - end[0]) * (start[1] - end[1]) - (start[0] - end[0]) * (y - end[1]);
    if (cross === 0) continue;
    const current = Math.sign(cross);
    if (sign !== 0 && current !== sign) return false;
    sign = current;
  }
  return true;
}

function shapeContains(symbol: PointSymbol, x: number, y: number, radius: number): boolean {
  if (symbol.shape === 'circle') return Math.hypot(x, y) <= radius;
  if (symbol.shape === 'square') return Math.max(Math.abs(x), Math.abs(y)) <= radius;
  return triangleContains(x, y, radius);
}

const pointImageCache = new Map<string, MapLibreRenderImage>();

function rasterisePointSymbol(symbol: PointSymbol): MapLibreRenderImage {
  const id = pointImageId(symbol);
  const cached = pointImageCache.get(id);
  if (cached) return cached;
  const pixelRatio = 2;
  const cssExtent = Math.ceil(symbol.radius + symbol.outlineWidth + 2);
  const size = cssExtent * 2 * pixelRatio;
  const data = new Uint8Array(size * size * 4);
  const fill = hexToRgb(symbol.fillColor);
  const outline = hexToRgb(symbol.outlineColor);
  const alpha = Math.round(symbol.opacity * 255);
  const centre = size / 2;
  for (let y = 0; y < size; y += 1) {
    for (let x = 0; x < size; x += 1) {
      const cssX = (x + 0.5 - centre) / pixelRatio;
      const cssY = (y + 0.5 - centre) / pixelRatio;
      if (symbol.shape === 'circle') {
        // Coverage over one texture texel, without changing geometry, sprite size or GPU layers.
        // Blend the fill/outline boundary as well as the outer edge. Only unique styles are cached.
        const distance = Math.sqrt(cssX * cssX + cssY * cssY);
        const outer = Math.max(
          0,
          Math.min(1, (symbol.radius + symbol.outlineWidth - distance) * pixelRatio + 0.5),
        );
        if (outer === 0) continue;
        const inner = Math.min(
          outer,
          Math.max(0, Math.min(1, (symbol.radius - distance) * pixelRatio + 0.5)),
        );
        const offset = (y * size + x) * 4;
        if (inner === outer || inner === 0) {
          const colour = inner === 0 ? outline : fill;
          data[offset] = colour[0];
          data[offset + 1] = colour[1];
          data[offset + 2] = colour[2];
        } else {
          for (let channel = 0; channel < 3; channel++) {
            data[offset + channel] = Math.round(
              (fill[channel]! * inner + outline[channel]! * (outer - inner)) / outer,
            );
          }
        }
        data[offset + 3] = Math.round(alpha * outer);
        continue;
      }
      const inFill = shapeContains(symbol, cssX, cssY, symbol.radius);
      const inOuter = shapeContains(symbol, cssX, cssY, symbol.radius + symbol.outlineWidth);
      if (!inOuter) continue;
      const colour = inFill ? fill : outline;
      const offset = (y * size + x) * 4;
      data[offset] = colour[0];
      data[offset + 1] = colour[1];
      data[offset + 2] = colour[2];
      data[offset + 3] = alpha;
    }
  }
  const image = { id, width: size, height: size, data, pixelRatio };
  pointImageCache.set(id, image);
  return image;
}

function thematicDefinitions(style: VectorLayerStyle): readonly ThematicSymbolDefinition[] {
  const classification = style.classification;
  if (classification.mode === 'single') return [];
  if (classification.mode === 'categorised') {
    return [...classification.categories.map((category) => category.symbol), classification.other];
  }
  return [
    ...classification.classes.map((numericClass) => numericClass.symbol),
    classification.nullSymbol,
  ];
}

function pointImagesForStyle(
  style: VectorLayerStyle,
  catalogue: PreparedSymbolCatalogue,
): readonly MapLibreRenderImage[] {
  const symbols = [
    style.symbols.point,
    ...thematicDefinitions(style).map(
      (definition) => effectiveSymbols(style.symbols, definition).point,
    ),
  ];
  return [
    ...new Map(
      symbols.map((symbol) => [
        pointImageId(symbol, catalogue),
        isImageGraphic(symbol.shape) ? assetImage(symbol, catalogue) : rasterisePointSymbol(symbol),
      ]),
    ).values(),
  ];
}

function backedPointExtent(symbol: PointSymbol, catalogue: PreparedSymbolCatalogue): number {
  const image = assetImage(symbol, catalogue);
  const edge = symbolDisplayLongEdge(image, symbol.radius * 2);
  const factor = edge / Math.max(image.width, image.height);
  return symbol.anchor === 'bottom'
    ? Math.hypot((image.width * factor) / 2, image.height * factor)
    : (Math.hypot(image.width, image.height) * factor) / 2;
}
function maximumPointSymbolExtent(
  style: VectorLayerStyle,
  catalogue: PreparedSymbolCatalogue,
): number {
  const symbols = [
    style.symbols.point,
    ...thematicDefinitions(style).map(
      (definition) => effectiveSymbols(style.symbols, definition).point,
    ),
  ];
  return Math.max(
    ...symbols.map((symbol) =>
      isImageGraphic(symbol.shape)
        ? symbol.backing?.enabled
          ? backedPointExtent(symbol, catalogue)
          : symbol.radius * (symbol.anchor === 'bottom' ? 2.3 : 1.5)
        : symbol.radius + symbol.outlineWidth,
    ),
  );
}

function renderLayersFor(
  layer: LayerDefinition,
  source: Extract<SourceDescriptor, { kind: 'vector' }>,
  scale: number,
  catalogue: PreparedSymbolCatalogue,
): AddLayerObject[] {
  const sourceId = sourceRenderIdForLayer(layer, source.id);
  const visibility = layer.visible ? 'visible' : 'none';
  const layers: AddLayerObject[] = [];

  if (source.geometry.types.some((type) => type === 'Polygon' || type === 'MultiPolygon')) {
    layers.push({
      id: layerRenderId(layer.id, 'polygon'),
      type: 'fill',
      source: sourceId,
      filter: geometryDisplayFilter(layer, 'Polygon'),
      layout: { visibility },
      paint: {
        'fill-color': classifiedValue(layer.style, (symbols) => symbols.polygon.fillColor),
        'fill-opacity': classifiedValue(layer.style, (symbols) => symbols.polygon.fillOpacity),
      },
    });
    layers.push({
      id: layerRenderId(layer.id, 'polygon-outline'),
      type: 'line',
      source: sourceId,
      filter: geometryDisplayFilter(layer, 'Polygon'),
      layout: { visibility, 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': classifiedValue(layer.style, (symbols) => symbols.polygon.outlineColor),
        'line-width': classifiedValue(layer.style, (symbols) =>
          presentationDimension(symbols.polygon.outlineWidth, scale, 0.1),
        ),
      },
    });
  }

  if (source.geometry.types.some((type) => type === 'LineString' || type === 'MultiLineString')) {
    layers.push({
      id: layerRenderId(layer.id, 'line'),
      type: 'line',
      source: sourceId,
      filter: geometryDisplayFilter(layer, 'LineString'),
      layout: { visibility, 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': classifiedValue(layer.style, (symbols) => symbols.line.color),
        'line-opacity': classifiedValue(layer.style, (symbols) => symbols.line.opacity),
        'line-width': classifiedValue(layer.style, (symbols) =>
          presentationDimension(symbols.line.width, scale, 0.1),
        ),
        'line-dasharray': classifiedValue(
          layer.style,
          (symbols) => MAPLIBRE_DASH_ARRAYS[symbols.line.dash],
        ),
        'line-offset': classifiedValue(layer.style, (symbols) => symbols.line.offset * scale),
      },
    });
  }

  if (source.geometry.types.some((type) => type === 'Point' || type === 'MultiPoint')) {
    const custom = [
      layer.style.symbols.point,
      ...thematicDefinitions(layer.style).map(
        (d) => effectiveSymbols(layer.style.symbols, d).point,
      ),
    ].some((p) => isImageGraphic(p.shape));
    layers.push({
      id: layerRenderId(layer.id, 'point'),
      type: 'symbol',
      source: sourceId,
      filter: geometryDisplayFilter(layer, 'Point'),
      layout: {
        visibility,
        'icon-image': classifiedValue(layer.style, (symbols) =>
          pointImageId(symbols.point, catalogue),
        ),
        ...(scale === 1 && !custom
          ? {}
          : {
              'icon-size': classifiedValue(layer.style, (symbols) =>
                isImageGraphic(symbols.point.shape)
                  ? (symbolDisplayLongEdge(
                      assetImage(symbols.point, catalogue),
                      2 * symbols.point.radius,
                    ) *
                      scale) /
                    (Math.max(
                      assetImage(symbols.point, catalogue).width,
                      assetImage(symbols.point, catalogue).height,
                    ) /
                      assetImage(symbols.point, catalogue).pixelRatio)
                  : presentationDimension(symbols.point.radius, scale, 0.5) / symbols.point.radius,
              ),
            }),
        ...(custom
          ? {
              'icon-rotate': classifiedValue(layer.style, (s) =>
                isImageGraphic(s.point.shape) ? (s.point.rotationDegrees ?? 0) : 0,
              ),
              'icon-anchor': classifiedValue(layer.style, (s) =>
                isImageGraphic(s.point.shape) ? (s.point.anchor ?? 'center') : 'center',
              ),
              'icon-rotation-alignment': 'viewport' as const,
              'icon-pitch-alignment': 'viewport' as const,
            }
          : {}),
        'icon-allow-overlap': true,
        'icon-ignore-placement': true,
      },
      ...(custom
        ? {
            paint: {
              'icon-opacity': classifiedValue(layer.style, (s) =>
                isImageGraphic(s.point.shape) && !s.point.backing?.enabled ? s.point.opacity : 1,
              ),
            },
          }
        : {}),
    });
  }

  return layers;
}

function selectableRenderLayersFor(
  layer: LayerDefinition,
  source: Extract<SourceDescriptor, { kind: 'vector' }>,
): readonly Pick<
  MapLibreSelectableLayer,
  'geometry' | 'highlightBeforeRenderLayerId' | 'renderLayerId' | 'renderSourceId'
>[] {
  if (!layer.visible) return [];
  const renderSourceId = sourceRenderIdForLayer(layer, source.id);
  const mappings: Pick<
    MapLibreSelectableLayer,
    'geometry' | 'highlightBeforeRenderLayerId' | 'renderLayerId' | 'renderSourceId'
  >[] = [];
  if (source.geometry.types.some((type) => type === 'Polygon' || type === 'MultiPolygon')) {
    // The fill is canonical for inspection; the duplicate outline is deliberately excluded.
    mappings.push({
      geometry: 'polygon',
      renderLayerId: layerRenderId(layer.id, 'polygon'),
      renderSourceId,
      highlightBeforeRenderLayerId: layerRenderId(layer.id, 'polygon-outline'),
    });
  }
  if (source.geometry.types.some((type) => type === 'LineString' || type === 'MultiLineString')) {
    const renderLayerId = layerRenderId(layer.id, 'line');
    mappings.push({
      geometry: 'line',
      renderLayerId,
      renderSourceId,
      highlightBeforeRenderLayerId: renderLayerId,
    });
  }
  if (source.geometry.types.some((type) => type === 'Point' || type === 'MultiPoint')) {
    const renderLayerId = layerRenderId(layer.id, 'point');
    mappings.push({
      geometry: 'point',
      renderLayerId,
      renderSourceId,
      highlightBeforeRenderLayerId: renderLayerId,
    });
  }
  return mappings;
}

function popupRenderLayersFor(
  layer: LayerDefinition,
  source: Extract<SourceDescriptor, { kind: 'vector' }>,
): readonly Pick<
  MapLibrePopupLayer,
  'geometry' | 'highlightBeforeRenderLayerId' | 'renderLayerId' | 'renderSourceId'
>[] {
  return layer.popup.enabled &&
    isLayerPopupConfigurationValid(layer.popup, source.fields) &&
    popupConfigurationHasContent(layer.popup)
    ? selectableRenderLayersFor(layer, source)
    : [];
}

export const MAPLIBRE_INSPECTION_HIGHLIGHT_LAYER_ID = 'dropmap-inspection-highlight';

function inspectionFeatureFilter(
  geometry: InspectionHighlightGeometry,
  featureId: string | number,
): FilterSpecification {
  const geometryType =
    geometry === 'point' ? 'Point' : geometry === 'line' ? 'LineString' : 'Polygon';
  return ['all', geometryFilter(geometryType), ['==', '$id', featureId]] as FilterSpecification;
}

/** Builds one renderer-transient layer filtered to the authoritative inspected feature. */
export function createMapLibreInspectionHighlightLayer(
  scene: RenderScene,
  mapping: MapLibrePopupLayer,
  featureId: string | number,
): AddLayerObject | null {
  const layer = scene.layers.find((candidate) => candidate.id === mapping.layerId);
  if (!layer) return null;
  const filter = inspectionFeatureFilter(mapping.geometry, featureId);

  switch (mapping.geometry) {
    case 'point':
      return {
        id: MAPLIBRE_INSPECTION_HIGHLIGHT_LAYER_ID,
        type: 'circle',
        source: mapping.renderSourceId,
        filter,
        paint: {
          'circle-radius': classifiedValue(layer.style, (symbols) =>
            isImageGraphic(symbols.point.shape) && symbols.point.backing?.enabled
              ? backedPointExtent(symbols.point, scene.symbolImages ?? EMPTY_SYMBOL_CATALOGUE) + 4
              : symbols.point.radius + symbols.point.outlineWidth + 4,
          ),
          'circle-color': UI_ACCENT_COLOUR,
          'circle-opacity': 0.12,
          'circle-stroke-color': UI_ACCENT_COLOUR,
          'circle-stroke-opacity': 0.72,
          'circle-stroke-width': 2,
        },
      };
    case 'line':
      return {
        id: MAPLIBRE_INSPECTION_HIGHLIGHT_LAYER_ID,
        type: 'line',
        source: mapping.renderSourceId,
        filter,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': UI_ACCENT_COLOUR,
          'line-opacity': 0.35,
          'line-width': classifiedValue(layer.style, (symbols) => symbols.line.width + 5),
          'line-offset': classifiedValue(layer.style, (symbols) => symbols.line.offset),
        },
      };
    case 'polygon':
      return {
        id: MAPLIBRE_INSPECTION_HIGHLIGHT_LAYER_ID,
        type: 'line',
        source: mapping.renderSourceId,
        filter,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          'line-color': UI_ACCENT_COLOUR,
          'line-opacity': 0.58,
          'line-width': classifiedValue(layer.style, (symbols) => symbols.polygon.outlineWidth + 4),
        },
      };
  }
}

function renderLabelLayersFor(
  layer: LayerDefinition,
  source: Extract<SourceDescriptor, { kind: 'vector' }>,
  scale: number,
  catalogue: PreparedSymbolCatalogue,
): AddLayerObject[] {
  if (
    !layer.labels.enabled ||
    !layer.labels.field ||
    !isLayerLabelConfigurationValid(layer.labels, source.fields)
  ) {
    return [];
  }

  const sourceId = sourceRenderIdForLayer(layer, source.id);
  const field = layer.labels.field;
  const layers: AddLayerObject[] = [];

  if (source.geometry.types.some((type) => type === 'Point' || type === 'MultiPoint')) {
    const radialOffset =
      layer.labels.pointPlacement === 'centre'
        ? 0
        : ((maximumPointSymbolExtent(layer.style, catalogue) + layer.labels.pointOffset) * scale) /
          presentationDimension(layer.labels.textSize, scale, 2);
    const variableAnchors: ('bottom' | 'left' | 'right' | 'top')[] = [
      'top',
      'bottom',
      'right',
      'left',
    ];
    const placementLayout =
      layer.labels.pointPlacement === 'auto'
        ? {
            'text-variable-anchor': variableAnchors,
            'text-radial-offset': radialOffset,
          }
        : {
            'text-anchor': POINT_LABEL_ANCHORS[layer.labels.pointPlacement],
            'text-radial-offset': radialOffset,
          };
    layers.push({
      id: layerRenderId(layer.id, 'label-point'),
      type: 'symbol',
      source: sourceId,
      filter: labelFilter(layer, 'Point'),
      layout: {
        ...labelLayout(layer, field, scale),
        ...placementLayout,
      },
      paint: labelPaint(layer, scale),
    });
  }

  if (source.geometry.types.some((type) => type === 'LineString' || type === 'MultiLineString')) {
    layers.push({
      id: layerRenderId(layer.id, 'label-line'),
      type: 'symbol',
      source: layer.labels.lineOffset === 0 ? sourceId : lineLabelSourceRenderId(layer.id),
      filter: labelFilter(layer, 'LineString'),
      layout: {
        ...labelLayout(layer, field, scale),
        'symbol-placement': 'line',
        'symbol-spacing': LINE_LABEL_REPEAT_DISTANCE * scale,
        'text-keep-upright': true,
        // Displacement is baked into transient pixel-projected geometry, never glyph offsets.
        'text-offset': [0, 0],
      },
      paint: labelPaint(layer, scale),
    });
  }

  if (source.geometry.types.some((type) => type === 'Polygon' || type === 'MultiPolygon')) {
    layers.push({
      id: layerRenderId(layer.id, 'label-polygon'),
      type: 'symbol',
      source: polygonLabelSourceRenderId(sourceId),
      filter: labelFilter(layer, 'Point'),
      layout: {
        ...labelLayout(layer, field, scale),
        'symbol-placement': 'point',
        'text-anchor': 'center',
      },
      paint: labelPaint(layer, scale),
    });
  }

  return layers;
}

/**
 * Auto label priority currently follows the authored Layers-panel order. Keep
 * this resolution separate from geometry order so a future Placeio Maps priority
 * policy can change collision precedence without changing map stacking or
 * persisting MapLibre symbol-sort-key values.
 */
function layersInAutomaticLabelCollisionOrder(
  layers: readonly LayerDefinition[],
): readonly LayerDefinition[] {
  return [...layers].reverse();
}

function lineLabelRenderSource(
  layer: LayerDefinition,
  source: Extract<SourceDescriptor, { kind: 'vector' }>,
  scale: number,
  projection?: AnnotationScreenProjection,
): MapLibreRenderSource | null {
  if (
    !layer.labels.enabled ||
    !layer.labels.field ||
    layer.labels.lineOffset === 0 ||
    !isLayerLabelConfigurationValid(layer.labels, source.fields) ||
    !source.geometry.types.some((type) => type === 'LineString' || type === 'MultiLineString')
  )
    return null;
  // Projection-less plans are diagnostic only. Mounted/static renderers supply projection.
  const paths = projection
    ? lineLabelPaths(source.data, layer.labels.lineOffset * scale, projection)
    : dataForMapLibre(source.data);
  return {
    id: lineLabelSourceRenderId(layer.id),
    data: paths.data,
    sourceFeatureIndices: paths.sourceFeatureIndices,
    definition: {
      type: 'geojson',
      data: dataForMapLibre(paths.data).data,
      generateId: true,
      tolerance: 0.1,
    },
  };
}

/** Refresh just the camera-dependent label sources, retaining native symbol layers. */
export function createLineLabelRenderSources(
  scene: RenderScene,
  projection: AnnotationScreenProjection,
): readonly MapLibreRenderSource[] {
  return scene.layers.flatMap((layer) => {
    if (!layer.labels.enabled || !layer.labels.field || layer.labels.lineOffset === 0) return [];
    const source = effectiveVectorSourceForLayer(scene.sources, layer);
    const labels = source
      ? lineLabelRenderSource(layer, source, presentationScale(scene.presentationScale), projection)
      : null;
    return labels ? [labels] : [];
  });
}

export function createMapLibreRenderPlan(
  scene: RenderScene,
  projection?: AnnotationScreenProjection,
): MapLibreRenderPlan {
  const scale = presentationScale(scene.presentationScale);
  const annotationPlan = createAnnotationRenderPlan(
    scene.annotationLayers ?? [],
    scene.selectedAnnotationId,
    projection,
    scene.activeFocusAreaId,
    scene.annotationRenderScale,
    scale,
    scene.symbolImages ?? EMPTY_SYMBOL_CATALOGUE,
  );
  const vectorSources = scene.sources.filter(isVectorSource);
  const sourcesById = new Map(vectorSources.map((source) => [source.id, source]));
  const layerContexts = scene.layers.flatMap((layer) => {
    const canonical = sourcesById.get(layer.sourceId);
    const source = canonical ? effectiveVectorSourceForLayer(scene.sources, layer) : null;
    return source
      ? [{ layer, source, renderSourceId: sourceRenderIdForLayer(layer, source.id) }]
      : [];
  });
  const contextByLayerId = new Map(layerContexts.map((context) => [context.layer.id, context]));
  const renderSourceContexts = new Map<string, (typeof layerContexts)[number]>();
  for (const source of vectorSources) {
    const context = layerContexts.find(
      (candidate) => candidate.renderSourceId === sourceRenderId(source.id),
    );
    if (context) renderSourceContexts.set(context.renderSourceId, context);
  }
  for (const context of layerContexts) {
    if (!renderSourceContexts.has(context.renderSourceId)) {
      renderSourceContexts.set(context.renderSourceId, context);
    }
  }
  const sources: MapLibreRenderSource[] = [...renderSourceContexts.values()].map(
    ({ renderSourceId, source }) => {
      const rendererData = dataForMapLibre(source.data);
      return {
        id: renderSourceId,
        data: source.data,
        sourceFeatureIndices: rendererData.sourceFeatureIndices,
        definition: {
          type: 'geojson' as const,
          data: rendererData.data,
          generateId: true,
          // Keep curved/cased user lines smooth at broad scales while retaining tiled simplification.
          // Shared by Editor, Composer and export; canonical source geometry remains untouched.
          tolerance: 0.1,
        },
      };
    },
  );
  sources.push(
    ...annotationPlan.sources.map((source) => ({
      ...source,
      sourceFeatureIndices: source.data.features.map((_, index) => index),
    })),
  );
  for (const { layer, source } of layerContexts) {
    const labels = lineLabelRenderSource(layer, source, scale, projection);
    if (labels) sources.push(labels);
  }
  const polygonLabelContexts = new Map(
    layerContexts.flatMap((context) =>
      context.source.geometry.types.some((type) => type === 'Polygon' || type === 'MultiPolygon') &&
      context.layer.labels.enabled &&
      context.layer.labels.field &&
      isLayerLabelConfigurationValid(context.layer.labels, context.source.fields)
        ? [[context.renderSourceId, context] as const]
        : [],
    ),
  );
  for (const [renderSourceId, context] of polygonLabelContexts) {
    const anchors = polygonLabelAnchors(context.source.data);
    sources.push({
      id: polygonLabelSourceRenderId(renderSourceId),
      data: anchors,
      sourceFeatureIndices: anchors.features.map((_, index) => index),
      definition: { type: 'geojson', data: anchors },
    });
  }
  // The first layer in the Layers panel is visually topmost. MapLibre paints later
  // layers above earlier ones, so emit geometry in reverse panel order. Labels form
  // a second pass above all geometry; the top panel layer is emitted last so MapLibre's
  // reverse symbol-placement pass gives it collision priority.
  const geometryLayersInRenderOrder = [...scene.layers].reverse();
  const labelLayersInCollisionOrder = layersInAutomaticLabelCollisionOrder(scene.layers);
  const geometryLayers = geometryLayersInRenderOrder.flatMap((layer) => {
    const context = contextByLayerId.get(layer.id);
    return context
      ? renderLayersFor(layer, context.source, scale, scene.symbolImages ?? EMPTY_SYMBOL_CATALOGUE)
      : [];
  });
  const labelLayers = labelLayersInCollisionOrder.flatMap((layer) => {
    const context = contextByLayerId.get(layer.id);
    return context
      ? renderLabelLayersFor(
          layer,
          context.source,
          scale,
          scene.symbolImages ?? EMPTY_SYMBOL_CATALOGUE,
        )
      : [];
  });
  const layers = [
    ...annotationPlan.focusBasemapLayers,
    ...geometryLayers,
    ...labelLayers,
    ...annotationPlan.focusDataLayers,
    ...annotationPlan.layers,
  ];
  const popupLayers = scene.layers.flatMap((layer, stackIndex) => {
    const context = contextByLayerId.get(layer.id);
    return context
      ? popupRenderLayersFor(layer, context.source).map((mapping) => ({
          ...mapping,
          layerId: layer.id,
          sourceId: context.source.id,
          stackIndex,
        }))
      : [];
  });
  const selectableLayers = scene.layers.flatMap((layer, stackIndex) => {
    const context = contextByLayerId.get(layer.id);
    return context
      ? selectableRenderLayersFor(layer, context.source).map((mapping) => ({
          ...mapping,
          layerId: layer.id,
          sourceId: context.source.id,
          stackIndex,
        }))
      : [];
  });
  const images = [
    ...new Map(
      geometryLayersInRenderOrder.flatMap((layer) => {
        const source = contextByLayerId.get(layer.id)?.source;
        if (
          !source ||
          !source.geometry.types.some((type) => type === 'Point' || type === 'MultiPoint')
        ) {
          return [];
        }
        return pointImagesForStyle(layer.style, scene.symbolImages ?? EMPTY_SYMBOL_CATALOGUE).map(
          (image) => [image.id, image] as const,
        );
      }),
    ).values(),
    ...annotationPlan.images,
  ];

  return {
    sources,
    images: [...new Map(images.map((image) => [image.id, image])).values()],
    layers,
    popupLayers,
    selectableLayers,
    annotationSelectableLayerIds: annotationPlan.selectableLayerIds,
  };
}
